import React, { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_URL } from '../services/api';

const FADE_MS = 400;

const THEMES = [
  {
    name: 'mint-teal',
    baseFrom: '#a8edea',
    baseTo:   '#14b8a6',
    stops: ['#006d77', '#14b8a6', '#83eaf1', '#d1f9f4', '#a8edea', '#14b8a6'],
  },
  {
    name: 'deep-teal-lav',
    baseFrom: '#b5c7f2',
    baseTo:   '#14b8a6',
    stops: ['#0e7490', '#06b6d4', '#99f6e4', '#a7f3d0', '#b5c7f2', '#14b8a6'],
  },
];

export default function ReferralProgram() {
  const [themeIndex, setThemeIndex] = useState(0);
  const theme = THEMES[themeIndex % THEMES.length];

  // Cycle background color palette subtly every ~25s for visual interest
  useEffect(() => {
    const t = setInterval(() => setThemeIndex((i) => i + 1), 25000);
    return () => clearInterval(t);
  }, []);

  const staticStyles = `
    .flowing-background { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 0; overflow: hidden; pointer-events: none; background: linear-gradient(135deg, var(--base-from) 0%, var(--base-to) 100%); transition: background 500ms ease; }
    .gradient-layer { position: absolute; top: 0; left: 0; width: 200%; height: 200%; opacity: 0.8; mix-blend-mode: screen; filter: blur(40px); transition: background 500ms ease; }
    .gradient-layer-1 { background: conic-gradient(from 0deg at 50% 50%, var(--c1), var(--c2), var(--c3), var(--c4), var(--c5), var(--c1)); animation: rotate-flow-1 20s linear infinite; transform-origin: center center; }
    .gradient-layer-2 { background: conic-gradient(from 180deg at 30% 70%, var(--c2), var(--c4), var(--c3), var(--c5), var(--c1), var(--c2)); animation: rotate-flow-2 25s linear infinite reverse; transform-origin: 30% 70%; opacity: 0.6; }
    .gradient-layer-3 { background: conic-gradient(from 90deg at 70% 30%, var(--c3), var(--c5), var(--c2), var(--c4), var(--c1), var(--c3)); animation: rotate-flow-3 30s linear infinite; transform-origin: 70% 30%; opacity: 0.4; }
    @keyframes rotate-flow-1 { 0%{transform:rotate(0) scale(1)}25%{transform:rotate(90deg) scale(1.1)}50%{transform:rotate(180deg) scale(1)}75%{transform:rotate(270deg) scale(1.1)}100%{transform:rotate(360deg) scale(1)} }
    @keyframes rotate-flow-2 { 0%{transform:rotate(0) scale(1.2)}33%{transform:rotate(120deg) scale(1)}66%{transform:rotate(240deg) scale(1.1)}100%{transform:rotate(360deg) scale(1.2)} }
    @keyframes rotate-flow-3 { 0%{transform:rotate(0) scale(0.9)}40%{transform:rotate(144deg) scale(1.3)}80%{transform:rotate(288deg) scale(1)}100%{transform:rotate(360deg) scale(0.9)} }
    @media (prefers-reduced-motion: reduce) { .gradient-layer-1 { animation-duration: 60s; } .gradient-layer-2 { animation-duration: 80s; } .gradient-layer-3 { animation-duration: 100s; } }
    .demo-content { position: relative; z-index: 1; display: flex; min-height: 100vh; align-items: center; justify-content: center; padding: 24px; }
    .demo-card { background: rgba(255,255,255,0.1); backdrop-filter: blur(12px); border-radius: 24px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.25); padding: 32px; color: #fff; text-align: center; max-width: 720px; width: 100%; }
    .headline { font-size: clamp(22px, 4vw, 36px); font-weight: 800; margin: 0 0 8px; color: #000; text-shadow: none; }
    .subtext { font-size: clamp(14px, 2.6vw, 18px); opacity: .95; margin: 0; color: #000; }
    .qr-container { position: relative; width: 240px; height: 240px; margin: 12px auto 4px; }
    .qr-glow { position: absolute; inset: -12px; border-radius: 24px; background: radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,.35), rgba(255,255,255,0)); filter: blur(8px); }
    .qr-code { width: 100%; height: 100%; border-radius: 24px; border: 2px solid rgba(255,255,255,0.55); box-shadow: 0 20px 60px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.15); background: rgba(0,0,0,0.15); object-fit: cover; }
    .scan-text { margin-top: 12px; }
    /* ReferralProgram-only pill style per spec */
    .scan-pill {
      display: inline-block;
      border-radius: 9999px; /* fully curved edges */
      background: #FFFFFF; /* solid white */
      color: #0D0D0D; /* near-black */
      padding: 12px 20px; /* generous */
      font-family: Inter, "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-weight: 700; /* bold 600–700 */
      font-size: clamp(16px, 3.8vw, 18px); /* medium-large */
      letter-spacing: 0; /* normal kerning */
      line-height: 1; /* pill height harmony */
      /* soft drop shadow / glow with teal/blue tint */
      box-shadow:
        0 8px 20px rgba(20, 184, 166, 0.25), /* teal */
        0 0 18px rgba(30, 144, 255, 0.15),   /* blue glow */
        0 2px 6px rgba(0, 0, 0, 0.06);      /* subtle depth */
      border: 1px solid rgba(14, 165, 233, 0.10); /* faint cool border */
    }
  `;

  const dynamicVars = useMemo(() => `
    :root { --base-from: ${theme.baseFrom}; --base-to: ${theme.baseTo}; --c1: ${theme.stops[0]}; --c2: ${theme.stops[1]}; --c3: ${theme.stops[2]}; --c4: ${theme.stops[3]}; --c5: ${theme.stops[4]}; }
  `, [theme]);

  const [qrUrl, setQrUrl] = useState(null);
  const [, setExpiresAt] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [welcomeName, setWelcomeName] = useState('');
  const [isFading, setIsFading] = useState(false);
  const hideTimerRef = useRef(null);
  const socketRef = useRef(null);

  const resetTimer = (ms = 120000) => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setQrUrl(null);
      setExpiresAt(null);
    }, ms);
  };

  useEffect(() => {
    // Connect Socket.IO directly to backend API host
    console.log('[Socket] Connecting to', API_URL);
    const socket = io(API_URL, {
      transports: ['websocket', 'polling'],
      path: '/socket.io',
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Socket] Connected');
      socket.emit('join_qr_display');
    });
    socket.on('disconnect', (reason) => {
      console.warn('[Socket] Disconnected:', reason);
    });
    socket.on('connect_error', (err) => {
      console.warn('[Socket] Connect error:', err?.message || err);
    });
    if (socket && socket.io) {
      socket.io.on('reconnect_attempt', (n) => console.log('[Socket] Reconnect attempt:', n));
      socket.io.on('reconnect', (n) => console.log('[Socket] Reconnected:', n));
      socket.io.on('reconnect_error', (err) => console.warn('[Socket] Reconnect error:', err?.message || err));
      socket.io.on('reconnect_failed', () => console.warn('[Socket] Reconnect failed'));
    }
    socket.on('new_qr', (payload) => {
      const { qr_url, expires_at, landing_url, first_name, welcome_name } = payload || {};
      console.log('[QR] Full payload:', payload);
      if (landing_url) {
        console.log('[QR] Landing URL:', landing_url);
      }
      if (payload && payload.sms) {
        console.log('[Cron/SMS] Scheduled payload:', payload.sms);
        const { will_send, scheduled_for, has_phone, server_now } = payload.sms;
        try {
          const nowLocal = new Date();
          const nowUTC = new Date(nowLocal.toISOString());
          const srvUTC = server_now ? new Date(server_now) : null;
          const schedUTC = scheduled_for ? new Date(scheduled_for) : null;
          console.log('[Cron/SMS] Now (local):', nowLocal.toString());
          console.log('[Cron/SMS] Now (UTC):  ', nowUTC.toISOString());
          if (srvUTC) console.log('[Cron/SMS] Server now (UTC):', srvUTC.toISOString());
          if (schedUTC) {
            console.log('[Cron/SMS] Follow-up SMS scheduled for (UTC):', schedUTC.toISOString());
            const diffMs = schedUTC.getTime() - (srvUTC ? srvUTC.getTime() : nowUTC.getTime());
            const diffSec = Math.round(diffMs / 1000);
            console.log('[Cron/SMS] Seconds until due (server-based):', diffSec);
          }
        } catch (e) {
          console.warn('[Cron/SMS] Failed to compute schedule delta:', e);
        }
        if (!will_send && has_phone === false) {
          console.log('[Cron/SMS] No phone found on user; SMS will not be scheduled.');
        }
      }
      console.log('[QR Display] Incoming names:', { first_name, welcome_name });
      if (qr_url) {
        setIsFading(true);
        setTimeout(() => {
          setQrUrl(qr_url);
          setExpiresAt(expires_at || null);
          if (typeof first_name === 'string' && first_name.trim()) {
            const m = first_name.trim().match(/[A-Za-z][A-Za-z\-']*/);
            setFirstName(m ? m[0] : '');
          } else {
            setFirstName('');
          }
          if (typeof welcome_name === 'string' && welcome_name.trim()) {
            setWelcomeName(welcome_name.trim());
          } else {
            setWelcomeName('');
          }
          setIsFading(false);
          // Keep QR visible for 10 minutes
          resetTimer(120000);
          try {
            const computedFirst = (typeof first_name === 'string' && first_name.trim()) ? first_name.trim().match(/[A-Za-z][A-Za-z\-']*/)?.[0] || '' : '';
            const computedFromWelcome = (typeof welcome_name === 'string' && welcome_name.trim()) ? welcome_name.trim().match(/[A-Za-z][A-Za-z\-']*/)?.[0] || '' : '';
            const computedNameToShow = computedFirst || computedFromWelcome || '';
            console.log('[QR Display] Name to show (first only):', computedNameToShow);
          } catch {}
        }, FADE_MS);
      }
    });
    socket.on('qr_clear', () => {
      setIsFading(true);
      setTimeout(() => {
        setQrUrl(null);
        setExpiresAt(null);
        setFirstName('');
        setWelcomeName('');
        setIsFading(false);
      }, FADE_MS);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    });

    // Cron observability: listen for sms_sent / sms_failed
    socket.on('sms_sent', (evt) => {
      console.log('[Cron/SMS] Sent:', evt);
      try {
        if (evt?.server_now) console.log('[Cron/SMS] Server now at send (UTC):', new Date(evt.server_now).toISOString());
      } catch {}
    });
    socket.on('sms_failed', (evt) => {
      console.warn('[Cron/SMS] Failed:', evt);
      try {
        if (evt?.server_now) console.log('[Cron/SMS] Server now at failure (UTC):', new Date(evt.server_now).toISOString());
      } catch {}
    });

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      try {
        socket.off('new_qr');
        socket.off('qr_clear');
        socket.off('sms_sent');
        socket.off('sms_failed');
        if (socket && socket.io) {
          socket.io.off('reconnect_attempt');
          socket.io.off('reconnect');
          socket.io.off('reconnect_error');
          socket.io.off('reconnect_failed');
        }
        socket.disconnect();
      } catch {}
    };
  }, []);

  const fadingStyle = { opacity: isFading ? 0 : 1, transition: `opacity ${FADE_MS}ms ease` };
  // Always display ONLY the first name: prefer server-provided first_name,
  // else extract first token from welcome_name as a fallback.
  const nameToShow = React.useMemo(() => {
    if (firstName && firstName.trim()) return firstName.trim();
    if (welcomeName && welcomeName.trim()) {
      const m = welcomeName.trim().match(/[A-Za-z][A-Za-z\-']*/);
      return m ? m[0] : '';
    }
    return '';
  }, [firstName, welcomeName]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: staticStyles }} />
      <style dangerouslySetInnerHTML={{ __html: dynamicVars }} />

      <div className="flowing-background">
        <div className="gradient-layer gradient-layer-1" style={fadingStyle}></div>
        <div className="gradient-layer gradient-layer-2" style={fadingStyle}></div>
        <div className="gradient-layer gradient-layer-3" style={fadingStyle}></div>
      </div>

      <div className="demo-content">
        <div className="demo-card">
          {qrUrl ? (
            <div>
              {nameToShow ? (
                <p className="thankyou" style={{ marginBottom: 10, color: '#000', fontWeight: 800, fontSize: 'clamp(16px, 3.5vw, 24px)' }}>
                  Welcome, {nameToShow}!
                </p>
              ) : null}
              <div className="qr-container" style={fadingStyle}>
                <div className="qr-glow" />
                <img src={qrUrl} alt="Referral QR" className="qr-code" />
              </div>
              <p className="scan-text"><span className="scan-pill">Scan this code to view your referral code.</span></p>
            </div>
          ) : (
            <div style={fadingStyle}>
              <h1 className="headline">Welcome to Duluth Dental Center</h1>
              <p className="subtext">Ask us about our referral program</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
